import { paths } from '@/config/paths';
import { handleChunkLoadError, markChunkLoaded } from '@/utils/pwa/preloadErrorHandler';
import { createBrowserRouter, RouterProvider } from 'react-router';

import { AuthBoundary } from '@/components/shared/AuthBoundary/AuthBoundary';
import { ProtectedRoute } from '@/components/shared/ProtectedRoute/ProtectedRoute';

import { AppLayout } from '@/components/layouts/AppLayout/AppLayout';

import { ErrorBoundary } from '@/components/shared/ErrorBoundary/ErrorBoundary';
import { FullPageSpinner } from '@/components/shared/FullPageSpinner/FullPageSpinner';
import { ForgotPasswordRoute } from './routes/auth/ForgotPasswordRoute/ForgotPasswordRoute';
import { ForgotUsernameListRoute } from './routes/auth/ForgotUsernameListRoute/ForgotUsernameListRoute';
import { ForgotUsernameRoute } from './routes/auth/ForgotUsernameRoute/ForgotUsernameRoute';
import { LoginRoute } from './routes/auth/LoginRoute/LoginRoute';
import { RegisterRoute } from './routes/auth/RegisterRoute/RegisterRoute';
import { HomeRoute } from './routes/HomeRoute/HomeRoute';
import { NotFoundRoute } from './routes/NotFoundRoute/NotFoundRoute';

// eslint-disable-next-line react-refresh/only-export-components
export const lazyRoute = <T = unknown,>(importFn: () => Promise<T>, componentName: keyof T): (() => Promise<{ Component: T[keyof T] }>) => {
  const chunkName = String(componentName);

  return async () => {
    try {
      const module = await importFn();
      markChunkLoaded(chunkName);
      return { Component: module[componentName] };
    } catch {
      if (handleChunkLoadError(chunkName)) {
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        return new Promise(() => {});
      }
      throw new Error('Failed to load route chunk');
    }
  };
};

const router = createBrowserRouter([
  {
    path: '/',
    errorElement: <ErrorBoundary />,
    element: <AuthBoundary />,
    hydrateFallbackElement: <FullPageSpinner />,
    children: [
      {
        element: <ProtectedRoute requiredStatus='UNAUTHENTICATED' redirectTo={paths.home.path} />,
        children: [
          {
            path: paths.auth.login.path,
            element: <LoginRoute />,
          },
          {
            path: paths.auth.register.path,
            element: <RegisterRoute />,
          },
          {
            path: paths.auth.forgotPassword.path,
            element: <ForgotPasswordRoute />,
          },
          {
            path: paths.auth.forgotUsername.path,
            element: <ForgotUsernameRoute />,
          },
          {
            path: paths.auth.forgotUsernameList.path,
            element: <ForgotUsernameListRoute />,
          },
        ],
      },
      {
        element: <ProtectedRoute requiredStatus='AUTHENTICATED' redirectTo={paths.auth.login.path} />,
        children: [
          {
            element: <AppLayout />,
            children: [
              {
                path: paths.home.path,
                element: <HomeRoute />,
              },
              {
                path: paths.learningCenter.path,
                lazy: lazyRoute(() => import('./routes/LearningCenterRoute/LearningCenterRoute'), 'LearningCenterRoute'),
                handle: { isSubpage: true },
              },
              {
                path: paths.progressCenter.home.path,
                lazy: lazyRoute(() => import('./routes/progressCenter/ProgressCenterRoute/ProgressCenterRoute'), 'ProgressCenterRoute'),
              },
              {
                path: paths.progressCenter.reports.sessionSummary.path,
                lazy: lazyRoute(() => import('./routes/progressCenter/reports/SessionSummaryRoute/SessionSummaryRoute'), 'SessionSummaryRoute'),
                handle: { isSubpage: true },
              },
              {
                path: paths.progressCenter.reports.progress.path,
                lazy: lazyRoute(() => import('./routes/progressCenter/reports/ProgressReportRoute/ProgressReportRoute'), 'ProgressReportRoute'),
                handle: { isSubpage: true },
              },
              {
                path: paths.progressCenter.reports.assessment.path,
                lazy: lazyRoute(() => import('./routes/progressCenter/reports/AssessmentReportRoute/AssessmentReportRoute'), 'AssessmentReportRoute'),
                handle: { isSubpage: true },
              },
              {
                path: paths.progressCenter.reports.progressCheck.path,
                lazy: lazyRoute(() => import('./routes/progressCenter/reports/ProgressCheckReportRoute/ProgressCheckReportRoute'), 'ProgressCheckReportRoute'),
                handle: { isSubpage: true },
              },
              {
                path: paths.progressCenter.reports.postAssessment.path,
                lazy: lazyRoute(
                  () => import('./routes/progressCenter/reports/PostAssessmentReportRoute/PostAssessmentReportRoute'),
                  'PostAssessmentReportRoute',
                ),
                handle: { isSubpage: true },
              },
              {
                path: paths.progressCenter.reports.learningPlan.path,
                lazy: lazyRoute(() => import('./routes/progressCenter/reports/LearningPlanReportRoute/LearningPlanReportRoute'), 'LearningPlanReportRoute'),
                handle: { isSubpage: true },
              },
              {
                path: paths.managementPanel.home.path,
                lazy: lazyRoute(() => import('./routes/managementPanel/ManagementPanelRoute/ManagementPanelRoute'), 'ManagementPanelRoute'),
              },
              {
                path: paths.notifications.home.path,
                lazy: lazyRoute(() => import('./routes/notifications/NotificationsRoute/NotificationsRoute'), 'NotificationsRoute'),
                handle: { isSubpage: true },
              },
              {
                path: paths.notifications.details.path,
                lazy: lazyRoute(() => import('./routes/notifications/NotificationDetailsRoute/NotificationDetailsRoute'), 'NotificationDetailsRoute'),
                handle: { isSubpage: true },
              },
              {
                path: paths.managementPanel.documents.path,
                lazy: lazyRoute(() => import('./routes/managementPanel/DocumentsRoute/DocumentsRoute'), 'DocumentsRoute'),
                handle: { isSubpage: true },
              },
              {
                path: paths.managementPanel.students.list.path,
                lazy: lazyRoute(() => import('./routes/managementPanel/StudentsListRoute/StudentsListRoute'), 'StudentsListRoute'),
                handle: { isSubpage: true },
              },
              {
                path: paths.managementPanel.students.details.path,
                lazy: lazyRoute(() => import('./routes/managementPanel/StudentDetailsRoute/StudentDetailsRoute'), 'StudentDetailsRoute'),
                handle: { isSubpage: true },
              },
              {
                path: paths.managementPanel.notificationsPreferences.path,
                lazy: lazyRoute(
                  () => import('./routes/managementPanel/NotificationsPreferencesRoute/NotificationsPreferencesRoute'),
                  'NotificationsPreferencesRoute',
                ),
                handle: { isSubpage: true },
              },
              {
                path: paths.managementPanel.accountDetails.path,
                lazy: lazyRoute(() => import('./routes/managementPanel/AccountDetailsRoute/AccountDetailsRoute'), 'AccountDetailsRoute'),
                handle: { isSubpage: true },
              },
              {
                path: paths.managementPanel.billing.home.path,
                lazy: lazyRoute(() => import('./routes/managementPanel/BillingRoute/BillingRoute'), 'BillingRoute'),
                handle: { isSubpage: true },
              },
              {
                path: paths.managementPanel.billing.updatePaymentMethod.path,
                lazy: lazyRoute(() => import('./routes/managementPanel/UpdatePaymentMethodRoute/UpdatePaymentMethodRoute'), 'UpdatePaymentMethodRoute'),
                handle: { isSubpage: true },
              },
              {
                path: paths.managementPanel.communicationPreferences.path,
                lazy: lazyRoute(
                  () => import('./routes/managementPanel/CommunicationPreferencesRoute/CommunicationPreferencesRoute'),
                  'CommunicationPreferencesRoute',
                ),
                handle: { isSubpage: true },
              },
              {
                path: paths.scheduling.sessions.home.path,
                lazy: lazyRoute(() => import('./routes/scheduling/sessions/YourSessionsRoute/YourSessionsRoute'), 'YourSessionsRoute'),
              },
              {
                path: paths.scheduling.sessions.sessionsHistory.path,
                lazy: lazyRoute(() => import('./routes/scheduling/SessionsHistoryRoute/SessionsHistoryRoute'), 'SessionsHistoryRoute'),
                handle: { isSubpage: true },
              },
            ],
          },
        ],
      },
      {
        path: '*',
        element: <NotFoundRoute />,
      },
    ],
  },
  {
    path: paths.managementPanel.ketchPreferences.path,
    lazy: lazyRoute(() => import('./routes/managementPanel/KetchPreferencesRoute/KetchPreferencesRoute'), 'KetchPreferencesRoute'),
  },
]);

export const AppRouter = () => {
  return <RouterProvider router={router} />;
};
